import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Upload, FileText, Trash2, Loader2, File, Sparkles, ImageIcon } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useI18n } from '@/contexts/I18nContext';
import { toast } from 'sonner';
import api from '@/lib/api';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function MyDocuments() {
  const { t } = useI18n();
  const [apps, setApps] = useState([]);
  const [selectedApp, setSelectedApp] = useState('');
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState('passport');
  const [aiDialog, setAiDialog] = useState(false);
  const [aiProvider, setAiProvider] = useState('openai');
  const [aiProcessing, setAiProcessing] = useState(false);
  const [aiFile, setAiFile] = useState(null);
  const [aiResult, setAiResult] = useState(null);
  const [aiEnabled, setAiEnabled] = useState(false);

  const docTypes = [
    { value: 'passport', label: t('doc.passport') },
    { value: 'photo', label: t('doc.photo') },
    { value: 'proof_of_residence', label: t('doc.residence') },
    { value: 'birth_certificate', label: t('doc.birth') },
    { value: 'other', label: t('doc.other') },
  ];

  useEffect(() => {
    api.get('/applications/my').then(r => {
      setApps(r.data);
      if (r.data.length > 0) setSelectedApp(r.data[0].application_id);
    }).catch(() => {});
    // Check if AI is enabled
    api.get('/ai/settings').then(r => {
      setAiEnabled(r.data?.enabled || false);
      if (r.data?.provider) setAiProvider(r.data.provider);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedApp) {
      api.get(`/documents/${selectedApp}`).then(r => setDocs(r.data)).catch(() => {});
    }
  }, [selectedApp]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!selectedApp) { toast.error(t('doc.select')); return; }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('application_id', selectedApp);
    formData.append('doc_type', docType);

    setUploading(true);
    try {
      const r = await api.post('/documents/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setDocs(prev => [...prev, r.data]);
      toast.success('Document uploade');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur upload');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (docId) => {
    try {
      await api.delete(`/documents/${docId}`);
      setDocs(prev => prev.filter(d => d.document_id !== docId));
      toast.success('Document supprime');
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleSubmit = async () => {
    if (!selectedApp) return;
    try {
      await api.post(`/applications/${selectedApp}/submit`);
      toast.success('Dossier soumis avec succes !');
      api.get('/applications/my').then(r => setApps(r.data));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur soumission');
    }
  };

  const openAiDialog = () => {
    setAiFile(null);
    setAiResult(null);
    setAiDialog(true);
  };

  const handleAiFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) setAiFile(file);
  };

  const handleAiProcess = async () => {
    if (!aiFile) return;
    setAiProcessing(true);
    setAiResult(null);
    try {
      const formData = new FormData();
      formData.append('file', aiFile);
      formData.append('provider', aiProvider);
      const r = await api.post('/ai/process-photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      setAiResult(r.data);
      toast.success('Photo harmonisee avec succes !');
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors du traitement IA");
    } finally {
      setAiProcessing(false);
    }
  };

  const currentApp = apps.find(a => a.application_id === selectedApp);
  const canSubmit = currentApp && ['draft', 'correction_needed'].includes(currentApp.status) && docs.length > 0;

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-fade-in" data-testid="my-documents-page">
      <div>
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('doc.title')}</h1>
        <p className="text-muted-foreground text-sm mt-1">{t('doc.desc')}</p>
      </div>

      {apps.length === 0 ? (
        <Card className="border">
          <CardContent className="p-8 text-center">
            <p className="text-muted-foreground">Creez d'abord un dossier avant d'uploader des documents.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <Card className="border">
            <CardHeader>
              <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('doc.select')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Select value={selectedApp} onValueChange={setSelectedApp}>
                <SelectTrigger data-testid="select-application"><SelectValue placeholder={t('common.choose')} /></SelectTrigger>
                <SelectContent>
                  {apps.map(a => (
                    <SelectItem key={a.application_id} value={a.application_id}>
                      {a.personal_info?.full_name || 'Dossier'} - {t(`status.${a.status}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          <Card className="border">
            <CardHeader>
              <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('doc.upload_title')}</CardTitle>
              <CardDescription>{t('doc.upload_desc')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>{t('doc.type')}</Label>
                <Select value={docType} onValueChange={setDocType}>
                  <SelectTrigger data-testid="select-doc-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {docTypes.map(dt => <SelectItem key={dt.value} value={dt.value}>{dt.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-[#007FFF]/30 rounded-xl bg-[#007FFF]/5 hover:bg-[#007FFF]/10 cursor-pointer transition-colors" data-testid="upload-zone">
                <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={handleUpload} disabled={uploading} />
                {uploading ? (
                  <Loader2 className="w-8 h-8 animate-spin text-[#007FFF]" />
                ) : (
                  <>
                    <Upload className="w-8 h-8 text-[#007FFF] mb-2" />
                    <p className="text-sm text-muted-foreground">{t('doc.drop')}</p>
                  </>
                )}
              </label>

              {aiEnabled && (
                <Button variant="outline" className="w-full border-purple-200 text-purple-700 hover:bg-purple-50" onClick={openAiDialog} data-testid="ai-photo-btn">
                  <Sparkles className="w-4 h-4 mr-2" />
                  Harmoniser une photo avec l'IA
                </Button>
              )}
            </CardContent>
          </Card>

          {docs.length > 0 && (
            <Card className="border">
              <CardHeader>
                <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('doc.uploaded')} ({docs.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {docs.map(doc => (
                    <div key={doc.document_id} className="flex items-center justify-between p-3 rounded-lg border border-border">
                      <div className="flex items-center gap-3 min-w-0">
                        <File className="w-5 h-5 text-[#007FFF] shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{doc.file_name}</p>
                          <p className="text-xs text-muted-foreground capitalize">{docTypes.find(dt => dt.value === doc.type)?.label || doc.type}</p>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(doc.document_id)} data-testid={`delete-doc-${doc.document_id}`}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {canSubmit && (
            <Button className="w-full bg-[#009543] hover:bg-[#007A36] h-11" onClick={handleSubmit} data-testid="submit-application-btn">
              {t('doc.submit')}
            </Button>
          )}
        </>
      )}

      {/* AI Photo Processing Dialog */}
      <Dialog open={aiDialog} onOpenChange={setAiDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Sparkles className="w-5 h-5 text-purple-600" />
              Harmonisation IA de la photo
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              L'IA va ameliorer votre photo pour qu'elle corresponde aux standards d'une photo d'identite officielle (fond neutre, cadrage, eclairage).
            </p>
            <div className="space-y-2">
              <Label>Fournisseur IA</Label>
              <Select value={aiProvider} onValueChange={setAiProvider}>
                <SelectTrigger data-testid="ai-dialog-provider"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="openai">OpenAI (GPT Image 1)</SelectItem>
                  <SelectItem value="gemini">Gemini (Nano Banana)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Photo a traiter</Label>
              <label className="flex flex-col items-center justify-center w-full h-28 border-2 border-dashed border-purple-200 rounded-xl bg-purple-50 hover:bg-purple-100 cursor-pointer transition-colors">
                <input type="file" className="hidden" accept="image/*" onChange={handleAiFileSelect} data-testid="ai-file-input" />
                {aiFile ? (
                  <div className="flex items-center gap-2">
                    <ImageIcon className="w-5 h-5 text-purple-600" />
                    <span className="text-sm font-medium text-purple-700">{aiFile.name}</span>
                  </div>
                ) : (
                  <>
                    <Upload className="w-6 h-6 text-purple-400 mb-1" />
                    <span className="text-xs text-purple-500">Choisir une photo</span>
                  </>
                )}
              </label>
            </div>
            {aiResult && (
              <div className="space-y-2">
                <Label>Resultat</Label>
                <div className="rounded-lg border border-green-200 bg-green-50 p-3 flex items-center gap-3">
                  <img src={`${BACKEND}${aiResult.url}`} alt="AI processed" className="w-20 h-20 object-cover rounded-lg" />
                  <div>
                    <p className="text-sm font-medium text-green-800">Photo harmonisee !</p>
                    <p className="text-xs text-green-600">La photo a ete mise a jour sur votre profil.</p>
                  </div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAiDialog(false)}>{t('common.cancel')}</Button>
            <Button
              className="bg-purple-600 hover:bg-purple-700 text-white"
              onClick={handleAiProcess}
              disabled={!aiFile || aiProcessing}
              data-testid="ai-process-btn"
            >
              {aiProcessing ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Traitement en cours...</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-2" /> Harmoniser</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
